/**
 * Yleiskuva → one stage (Valmistelu, Toteutus, Tulokset): its checklist, with
 * each row linking to the page and section that fixes it, the one next step,
 * and — once the stage is done — the move to the next stage. The instance's
 * current stage is open; the others start collapsed. Nothing is locked.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { CheckCircle2, Circle, ChevronDown, ArrowRight, Terminal } from 'lucide-react';
import ScriptHint from '../../components/ScriptHint/ScriptHint';
import { itemHref } from '../../utils/stageStatus';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Card = styled.section`
  border-radius: 14px;
  border: 1px solid ${p => (p.$current ? 'rgba(0, 153, 255, 0.45)' : 'rgba(255, 255, 255, 0.08)')};
  background: ${p => (p.$current ? 'rgba(0, 153, 255, 0.06)' : 'rgba(255, 255, 255, 0.02)')};
  overflow: hidden;
`;

const Head = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 18px;
  background: none;
  border: none;
  color: #fff;
  text-align: left;
  cursor: pointer;

  h2 { margin: 0; font-size: 17px; flex: 1; }
  .badge { font-size: 11px; padding: 2px 8px; border-radius: 10px; background: #0099ff; color: #fff; }
  .progress { font-size: 13px; color: rgba(255, 255, 255, 0.6); font-variant-numeric: tabular-nums; }
  svg.chev { transition: transform 0.2s; transform: rotate(${p => (p.$open ? '180deg' : '0deg')}); opacity: 0.6; }
`;

const Body = styled.div`
  padding: 0 18px 18px;
`;

export const Items = styled.ul`
  list-style: none;
  margin: 0 0 14px;
  padding: 0;
`;

const Item = styled.li`
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 14px;

  .row { display: flex; align-items: center; gap: 10px; }
  a { color: ${p => (p.$done ? 'rgba(255,255,255,0.75)' : '#fff')}; text-decoration: none; flex: 1; }
  a:hover { text-decoration: underline; }
  .optional { font-size: 11px; color: rgba(255, 255, 255, 0.4); }
  .script { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; color: #fbbf24; background: none; border: none; cursor: pointer; }
`;

const actionStyle = `
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 9px 16px;
  border: none;
  border-radius: 8px;
  background: #2563eb;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;

  &:hover { filter: brightness(1.1); }
  &:disabled { opacity: 0.6; cursor: wait; }
`;

const Next = styled(Link)`${actionStyle}`;
const Move = styled.button`${actionStyle}`;

const Muted = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
`;

export function ChecklistItem({ item, instanceId }) {
  const { t } = useTranslation();
  const [showScript, setShowScript] = useState(false);
  const Icon = item.done ? CheckCircle2 : Circle;
  return (
    <Item $done={item.done}>
      <div className="row">
        <Icon size={16} color={item.done ? '#4ade80' : item.optional ? '#666' : '#fbbf24'} />
        <Link to={itemHref(item, instanceId)}>{t(`stageStatus.${item.key}`, item.values || {})}</Link>
        {item.script && !item.done && (
          <button type="button" className="script" onClick={() => setShowScript((v) => !v)} aria-expanded={showScript}>
            <Terminal size={12} /> {t('stageStatus.byScript')}
          </button>
        )}
        {item.optional && <span className="optional">{t('stageStatus.optional')}</span>}
      </div>
      {showScript && <ScriptHint command={item.script.command} skill={item.script.skill} />}
    </Item>
  );
}

/**
 * @param {{ stageKey: string, summary: object, current: boolean, instanceId: string,
 *   nextStageKey?: string, onMove?: () => void, moving?: boolean }} props
 */
export default function StageCard({ stageKey, summary, current, instanceId, nextStageKey, onMove, moving }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(current);

  return (
    <Card $current={current}>
      <Head type="button" $open={open} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <h2>{t(`adminHome.stage_${stageKey}`)}</h2>
        {current && <span className="badge">{t('adminHome.currentStage')}</span>}
        <span className="progress">{summary.done}/{summary.total}</span>
        <ChevronDown size={18} className="chev" />
      </Head>
      {open && (
        <Body>
          <Items>
            {summary.items.map((item) => <ChecklistItem key={item.key} item={item} instanceId={instanceId} />)}
          </Items>
          {summary.next && (
            <Next to={itemHref(summary.next, instanceId)}>
              {t('adminHome.nextStep')}: {t(`stageStatus.${summary.next.key}`, summary.next.values || {})} <ArrowRight size={16} />
            </Next>
          )}
          {!summary.next && nextStageKey && current && (
            <Move type="button" onClick={onMove} disabled={moving}>
              {t(`adminHome.moveTo_${nextStageKey}`)} <ArrowRight size={16} />
            </Move>
          )}
          {!summary.next && !nextStageKey && <Muted>{t('adminHome.stageDone')}</Muted>}
        </Body>
      )}
    </Card>
  );
}
